<script setup lang="ts">
import { isAfter, parseISO } from 'date-fns'

definePageMeta({
  middleware: ['app-check'],
  layout: 'empty'
})

useHead({
  bodyAttrs: {
    // override the background color until challenge webviews support dark mode
    class: 'dark:tw-bg-riser-mainBackground'
  }
})

const { nativeShare } = useNativeClientService()
const { getChallenge, joinChallenge, leaveChallenge } = useChallengesService()

const { t, locale } = useI18n()

const authStore = useAuthStore()

const { params } = useRoute()

if (!params.id) {
  throw createError({ statusCode: 404, statusMessage: 'Challenge not found', fatal: true })
}

const {
  data: challenge,
  pending: challengeLoading,
  refresh: reloadChallenge
} = await useAsyncData(() => getChallenge(params.id as string))

const state = reactive({
  joinLoading: false
})

const hydrated = ref(false)

const isLoggedIn = computed(() => !!authStore.session_data.user)

const shareLink = computed(() => {
  if (!challenge.value) return

  return `https://riserapp.com/challenge/${challenge.value.id}`
})

const hasGoals = computed(() => {
  if (!challenge.value) return false

  if (!challenge.value.goals) return false

  return (
    (challenge.value.goals.distance && challenge.value.goals.distance.length > 0) ||
    (challenge.value.goals.geo && challenge.value.goals.geo.length > 0) ||
    (challenge.value.goals.trip_sharing && challenge.value.goals.trip_sharing.length > 0)
  )
})

const title = computed(() => {
  if (!challenge.value) return

  return resolveChallengeField(challenge.value, 'title', locale.value)
})

const headerPhoto = computed(() => {
  if (!challenge.value) return

  return (
    resolveChallengeField(challenge.value, 'cover_photo', locale.value) ||
    '/images/placeholder/challenge.png'
  )
})

const currentUserRank = computed(() => {
  if (!isLoggedIn.value || !challenge.value) return

  return challenge.value.ranking.find((rank) => rank.user_id === authStore.currentUser?.id)
})

const headerUnitLabel = computed(() => {
  if (!challenge.value) return null

  const key = challengeScoreLabelKey(challenge.value.type)

  return key ? t(key) : null
})

const joined = computed(() => {
  return challenge.value?.joined
})

const isPast = computed(() => {
  if (!challenge.value) return false

  return isAfter(new Date(), parseISO(challenge.value.end_date))
})

const canJoin = computed(() => {
  if (!hydrated.value) return false

  if (!challenge.value) return false

  if (challengeLoading.value) return false

  return isLoggedIn.value && !isPast.value
})

const joinButtonLabel = computed(() => {
  if (!hydrated.value) return '-'

  if (!challenge.value) return '-'

  if (challengeLoading.value) return '-'

  return joined.value ? t('Leave Challenge') : t('Accept the Challenge')
})

const share = () => {
  if (import.meta.server || !title.value || !shareLink.value) return

  nativeShare(title.value, t('Accept the Challenge'), shareLink.value)
}

const toggleJoin = async () => {
  if (joined.value) {
    await leave()
  } else {
    await join()
  }
}

const join = async () => {
  if (!challenge.value) return

  try {
    state.joinLoading = true

    await joinChallenge(challenge.value.id)

    await reloadChallenge()
  } finally {
    state.joinLoading = false
  }
}

const leave = async () => {
  if (!challenge.value) return

  try {
    state.joinLoading = true

    await leaveChallenge(challenge.value.id)

    await reloadChallenge()
  } finally {
    state.joinLoading = false
  }
}

// The page is server-rendered anonymously and hydrates with
// that payload. During hydration useAsyncData reuses the payload and won't
// re-run the handler, so we reload onNuxtReady — which fires once hydration has finished.
// By then refresh() actually hits the API and loads the authenticated data.
onNuxtReady(() => {
  hydrated.value = true

  reloadChallenge()
})
</script>

<template>
  <div v-if="challenge" class="tw-flex tw-flex-col tw-flex-wrap tw-pb-4">
    <div class="tw-flex tw-w-full tw-flex-grow tw-flex-wrap">
      <section class="tw-mx-auto tw-mt-5 tw-w-full tw-px-4">
        <div class="card tw-mb-5 tw-overflow-hidden">
          <div class="tw-relative tw-w-full">
            <img :src="headerPhoto" class="tw-w-full" style="aspect-ratio: 320/179" />

            <section class="tw-grid tw-grid-cols-3 tw-py-3">
              <div class="tw-text-center">
                <p class="tw-text-xl tw-font-bold tw-leading-none">
                  <ClientOnly>
                    <template #default>
                      {{ currentUserRank?.rank ?? '-' }}
                    </template>

                    <template #fallback>
                      <span>-</span>
                    </template>
                  </ClientOnly>
                </p>

                <span class="tw-text-sm tw-text-gray-600">{{ t('Your Rank') }}</span>
              </div>

              <div class="tw-text-center">
                <p class="tw-text-xl tw-font-bold tw-leading-none">
                  <ClientOnly>
                    <template #default>
                      <ChallengeUnitRenderer
                        v-if="challenge && currentUserRank"
                        :challenge-type="challenge.type"
                        :rank="currentUserRank"
                        unit-classes="tw-text-sm tw-leading-none tw-text-gray-600 tw-font-normal"
                        climb-unit-imperical="ft"
                        climb-unit-metric="m"
                      />

                      <template v-else>
                        <span>-</span>
                      </template>
                    </template>

                    <template #fallback>
                      <span>-</span>
                    </template>
                  </ClientOnly>
                </p>

                <span class="tw-text-sm tw-text-gray-600">
                  {{ headerUnitLabel }}
                </span>
              </div>

              <div class="tw-text-center">
                <p class="tw-text-xl tw-font-bold tw-leading-none">
                  <ClientOnly>
                    <template #default>
                      {{ challenge?.participants ?? '-' }}
                    </template>

                    <template #fallback>
                      <span>-</span>
                    </template>
                  </ClientOnly>
                </p>

                <span class="tw-text-sm tw-text-gray-600">{{ t('Participants') }}</span>
              </div>
            </section>
          </div>
        </div>

        <div class="tw-flex tw-flex-wrap">
          <div class="tw-flex tw-w-full tw-flex-col tw-gap-5">
            <section v-if="isPast == false" class="card tw-overflow-hidden">
              <div class="tw-flex">
                <InputAppButton
                  :loading="state.joinLoading"
                  :disabled="!canJoin"
                  class="tw-w-7/12 tw-flex-grow tw-items-center tw-justify-center tw-truncate tw-border-r tw-px-6 tw-py-4 tw-text-sm tw-font-semibold tw-transition-all tw-duration-300"
                  :class="hydrated && canJoin && !joined ? 'tw-bg-riser-blue tw-text-white' : ''"
                  @click="toggleJoin"
                >
                  {{ joinButtonLabel }}
                </InputAppButton>

                <InputAppButton
                  class="tw-flex tw-w-5/12 tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-4 tw-text-sm tw-font-semibold"
                  @click="share"
                >
                  <FontAwesomeIcon icon="share" />

                  {{ t('Share') }}
                </InputAppButton>
              </div>
            </section>

            <section class="card tw-px-6 tw-py-6">
              <h1 class="tw-mb-4 tw-text-xl tw-font-semibold tw-uppercase lg:tw-text-2xl">
                {{ title }}
              </h1>

              <ChallengeContent :challenge="challenge" />
            </section>

            <section v-if="challenge && hasGoals && joined" class="card tw-px-6 tw-py-6">
              <h2 class="tw-mb-2 tw-text-xl tw-font-bold">{{ t('Goals') }}</h2>

              <ChallengeGoalsSection :challenge="challenge" />
            </section>

            <section
              v-if="challenge && challenge.influencers_challenge && joined && currentUserRank"
              class="card tw-px-6 tw-py-6"
            >
              <h2 class="tw-mb-1 tw-font-bold">
                {{ t('Direct competition') }}
              </h2>

              <ChallengeDirectCompetition :challenge="challenge" />
            </section>

            <section v-if="challenge?.ranking?.length" class="card tw-px-6 tw-py-6">
              <h2 class="tw-mb-1 tw-text-xl tw-font-bold">
                {{ t('Leaderboard') }}
              </h2>

              <ChallengeLeaderboard :challenge="challenge" />
            </section>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div v-else>
    <LoadingIndicator />
  </div>
</template>
